import React, { ReactNode, useState } from 'react';
import { Flex, DeprecatedIcon, Typography, Box, Popover } from '@fullstory/components';
import { useHover } from '@fullstory/react-hooks';
import { Frequency } from '@fullstory/client-api';
import { handleInteractiveKeydown } from '@fullstory/util/a11y';
import css from './index.module.scss';
import { classNames } from '@fullstory/util/fmt';
import { FormattedMessage } from 'react-intl';
import { intl } from '@fullstory/intl';

interface SegmentFollowProps extends FrequencyTextProps {
  isFollowing?: boolean;
  onSubscribeToWeeklyDigest?: () => void;
  onAddToDigest?: () => void;
  onRemoveFromDigest?: () => void;
  onSettingsClick: () => void;
}

interface FrequencyTextProps {
  digestFrequency: Frequency;
  onSettingsClick: () => void;
}

const digestFrequencyString = {
  [Frequency.DigestDaily]: intl.formatMessage({ defaultMessage: 'daily', id: 'b1MFe5' }),
  [Frequency.DigestOff]: intl.formatMessage({ defaultMessage: 'off', id: 'C0MnAi' }),
  [Frequency.DigestWeekly]: intl.formatMessage({ defaultMessage: 'weekly', id: 'rP7VxA' }),
};

const FrequencyText: React.FC<FrequencyTextProps> = ({ digestFrequency, onSettingsClick }) => {
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const settingsButton = (content: ReactNode) => (
    <div
      role="button"
      tabIndex={0}
      onClick={onSettingsClick}
      onKeyDown={handleInteractiveKeydown(onSettingsClick)}
    >
      <Typography variant="body3" color="blue" className={css.segmentFollow}>
        {content}
      </Typography>
    </div>
  );

  return (
    <>
      <span
        ref={setAnchorEl}
        role="button"
        tabIndex={0}
        onClick={() => setOpen(open => !open)}
        onKeyDown={handleInteractiveKeydown(() => setOpen(open => !open))}
      >
        <Typography color="neutrals-n7" variant="body3" textTransform="capitalize">
          &nbsp;&middot;&nbsp;{digestFrequencyString[digestFrequency]}
        </Typography>
      </span>
      <Popover
        anchor={anchorEl}
        onCloseSignal={() => setOpen(false)}
        placement="bottom-end"
        open={open}
        arrow
      >
        <Box padding={[8, 12]}>
          <Flex column>
            <Typography variant="body3">
              <FormattedMessage
                defaultMessage="Frequency can be modified in <settingsButton>Notification Settings.</settingsButton>"
                id="YO+1gk"
                values={{ settingsButton }}
              />
            </Typography>
          </Flex>
        </Box>
      </Popover>
    </>
  );
};

export const SegmentFollow: React.FC<SegmentFollowProps> = ({
  isFollowing,
  onSubscribeToWeeklyDigest,
  onAddToDigest,
  onRemoveFromDigest,
  digestFrequency,
  onSettingsClick,
}) => {
  const { hovered, hoverProps } = useHover();

  return digestFrequency === Frequency.DigestOff ? (
    <Flex
      justifyContent="flex-end"
      onClick={onSubscribeToWeeklyDigest}
      className={classNames(css.segmentFollow, css.notificationsContainer)}
    >
      <DeprecatedIcon color="neutrals-n7" name="notifications" />
      <Typography color="neutrals-n7" variant="body3" textTransform="capitalize">
        &nbsp;&middot;&nbsp;{digestFrequencyString[digestFrequency]}
      </Typography>
    </Flex>
  ) : isFollowing ? (
    <Flex justifyContent="flex-end" className={css.segmentFollow}>
      <Flex {...hoverProps} onClick={onRemoveFromDigest}>
        {!hovered ? (
          <>
            <DeprecatedIcon
              color="pink-base"
              size={16}
              name="checkmark"
              grammarUIPropOverrides={{ color: 'base-objects-default' }}
            />
            <Box marginLeft={8} width={55}>
              <Typography variant="heading5">
                <FormattedMessage defaultMessage="In digest" id="8NmgYC" />
              </Typography>
            </Box>
          </>
        ) : (
          <>
            <DeprecatedIcon
              color="pink-base"
              size={16}
              name="close"
              grammarUIPropOverrides={{ color: 'base-objects-default' }}
            />
            <Box marginLeft={8} width={52}>
              <Typography variant="heading5">
                <FormattedMessage
                  defaultMessage="Remove"
                  id="Y8Pjdq"
                  description="Remove from digest"
                />
              </Typography>
            </Box>
          </>
        )}
      </Flex>
      <FrequencyText digestFrequency={digestFrequency} onSettingsClick={onSettingsClick} />
    </Flex>
  ) : (
    <Flex
      justifyContent="flex-end"
      onClick={onAddToDigest}
      className={classNames(css.segmentFollow, css.notificationsContainer)}
    >
      <DeprecatedIcon
        color="pink-base"
        name="notifications"
        grammarUIPropOverrides={{ color: 'base-objects-default' }}
      />
      <Box marginLeft={8}>
        <Typography variant="body3">
          <FormattedMessage defaultMessage="Add to digest" id="degY3y" />
        </Typography>
      </Box>
    </Flex>
  );
};
